"use client";

import dynamic from "next/dynamic";
import type { EducationSection } from "./types";
import type { ReactNode } from "react";

// Chart.js must not execute during static-build SSR — same pattern as the simulator.
const SemanticChart = dynamic(() => import("@/components/ui/SemanticChart"), {
  ssr: false,
});

interface ComparisonEducationProps {
  sections: EducationSection[];
}

function renderInlineEvidence(paragraph: string): ReactNode[] {
  return paragraph
    .split(/(\[[^\]]+\]\(https?:\/\/[^)\s]+\)|\*\*[^*]+\*\*)/g)
    .map((part, index) => {
      const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
      if (link) {
        return <a key={index} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>;
      }
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
}

/**
 * The teaching layer of a comparison page: short narrative sections with
 * data charts interleaved where the explanation needs them. Paragraphs are
 * prerendered HTML (SEO); only the chart canvas hydrates client-side.
 */
export default function ComparisonEducation({ sections }: ComparisonEducationProps) {
  if (!sections || sections.length === 0) return null;

  return (
    <section className="section education-section">
      <h2 className="education-title">What this choice actually costs you</h2>
      {sections.map((sec, idx) => (
        <div key={idx} className="education-block">
          <h3 className="education-heading">{sec.heading}</h3>
          {sec.paragraphs.map((p, pIdx) => (
            <p key={pIdx} className="education-paragraph">
              {renderInlineEvidence(p)}
            </p>
          ))}
          {sec.chart && <SemanticChart chartData={sec.chart} />}
        </div>
      ))}
      <style jsx>{`
        .education-section {
          margin-top: 0;
          margin-bottom: 0;
        }
        .education-title {
          font-family: var(--font-heading);
          font-size: var(--text-2xl);
          color: var(--text-primary);
          font-weight: var(--fw-bold);
          margin-top: 0;
          margin-bottom: var(--space-4);
          letter-spacing: var(--tracking-tight);
        }
        .education-block {
          margin-bottom: var(--space-6);
        }
        .education-block:last-child {
          margin-bottom: 0;
        }
        .education-heading {
          font-family: var(--font-heading);
          font-size: var(--text-lg);
          color: var(--text-primary);
          font-weight: var(--fw-bold);
          margin-bottom: var(--space-3);
          line-height: var(--lh-snug);
        }
        .education-paragraph {
          font-family: var(--font-body);
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.7;
          margin-bottom: var(--space-3);
          font-weight: var(--fw-medium);
        }
        @media (max-width: 767px) {
          .education-heading {
            font-size: var(--text-md);
          }
        }
      `}</style>
    </section>
  );
}
